import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { motion } from 'framer-motion';
import { Avatar } from '../../common/designSystem';

/**
 * Recently joined employees — avatar, name, email and joining date.
 *
 * Rows stagger in so the list assembles rather than appearing all at once.
 */
const RecentJoinersList = ({ title, joiners, emptyLabel, loading, limit }) => {
  const rows = (joiners || []).slice(0, limit);

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder rounded-2xl p-4 md:p-5 flex flex-col h-full">
      <h4 className="text-base font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg mb-1">
        {title}
      </h4>

      {loading || !rows.length ? (
        <div className="flex-1 flex items-center justify-center min-h-[120px]">
          <span className="text-xs text-shoorah-gray3">{loading ? 'Loading…' : emptyLabel}</span>
        </div>
      ) : (
        <ul className="flex flex-col">
          {rows.map((joiner, index) => (
            <motion.li
              key={joiner?._id || `${joiner?.email}-${index}`}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.06 }}
              className="flex items-center gap-3 py-[10px] border-b border-shoorah-borderGray dark:border-shoorah-darkBorder last:border-b-0"
            >
              <Avatar
                src={joiner?.userProfile || joiner?.user_profile || joiner?.profile || joiner?.image}
                name={joiner?.name}
                className="w-9 h-9 text-xs"
              />

              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg truncate">
                  {joiner?.name || '—'}
                </p>
                <p className="text-xs text-shoorah-gray3 truncate" title={joiner?.email}>
                  {joiner?.email}
                </p>
              </div>

              {joiner?.createdAt ? (
                <span className="text-xs text-shoorah-gray3 tabular-nums flex-shrink-0">
                  {moment(joiner.createdAt).format('DD MMM YYYY')}
                </span>
              ) : null}
            </motion.li>
          ))}
        </ul>
      )}
    </div>
  );
};

RecentJoinersList.propTypes = {
  title: PropTypes.string.isRequired,
  joiners: PropTypes.array,
  emptyLabel: PropTypes.string,
  loading: PropTypes.bool,
  limit: PropTypes.number
};

RecentJoinersList.defaultProps = {
  emptyLabel: 'No one has joined yet',
  limit: 5
};

export default RecentJoinersList;
